import { useCallback, useEffect, useRef, useState } from 'react';

// Autoplay permission belongs to this document, never to a shared localStorage flag.
export function useNotificationAudio(audioRef, enabled) {
  const [status, setStatus] = useState('blocked');
  const pending = useRef(null);
  const generation = useRef(0);
  const attemptPlay = useCallback((ignoreEnabled = false) => {
    const audio = audioRef.current;
    if ((!enabled && !ignoreEnabled) || !audio) return Promise.resolve(false);
    if (pending.current) return pending.current;
    // Burst events can share a playing bell; never rewind/cut off the current alert.
    if (!audio.paused && !audio.ended) {
      setStatus('ready');
      return Promise.resolve(true);
    }
    const current = generation.current;
    let result;
    try { result = audio.play(); } catch (error) { result = Promise.reject(error); }
    const attempt = Promise.resolve(result).then(() => {
      if (generation.current === current) setStatus('ready');
      return true;
    }).catch(error => {
      if (generation.current === current) {
        setStatus(error.name === 'NotAllowedError' ? 'blocked' : 'error');
        console.warn('Notification sound could not play:', error.name);
      }
      return false;
    }).finally(() => { if (pending.current === attempt) pending.current = null; });
    pending.current = attempt;
    return attempt;
  }, [audioRef, enabled]);

  const play = useCallback(() => attemptPlay(false), [attemptPlay]);
  const preview = useCallback(() => attemptPlay(true), [attemptPlay]);

  const unlock = useCallback(() => {
    if (status !== 'ready') return play();
    return Promise.resolve(true);
  }, [play, status]);

  useEffect(() => {
    const audio = audioRef.current;
    if (enabled) setStatus('blocked');
    return () => {
      generation.current += 1;
      pending.current = null;
      if (audio && !audio.paused) audio.pause();
    };
  }, [audioRef, enabled]);

  useEffect(() => {
    if (!enabled || status === 'ready') return;
    // Keep these installed after rejection so the next real gesture can retry.
    const gesture = () => { unlock(); };
    document.addEventListener('click', gesture);
    document.addEventListener('keydown', gesture);
    return () => {
      document.removeEventListener('click', gesture);
      document.removeEventListener('keydown', gesture);
    };
  }, [enabled, status, unlock]);

  return { status, play, preview, unlock };
}
